import { AvailableBushings } from './CrusherConeCHInfoData';
import { CrusherTypes } from './CrusherConeCHTypes';
import type { ConeCrusherCHUnitInfoDataMapInterface } from './unitInfoData';

/**
 *
 *
 * @param model
 * @param mantle
 * @param concave
 * @param selectedThrow
 */
export const CHCrusherGetMaxCSS = (
  modelName: string,
  mantleName: string,
  concaveName: string,
  selectedThrow: number,
  unitInfoData: ConeCrusherCHUnitInfoDataMapInterface,
) => {
  const model = unitInfoData[modelName];
  const concave = model.concaves[concaveName];
  const mantle = concave.mantles[mantleName];
  const nss = mantle?.nss;
  const oilValue = model.oil;
  const cssMax = nss - selectedThrow / 2 - oilValue / 1.74;
  return Math.ceil(cssMax);
};

/**
 *
 * @param model
 * @param concave
 * @param useBinFeeder
 * @param useOB
 * @param factorF100
 * @param factorF90
 * @param factorF85
 */
export const GetMaxFeedSize = (
  model: string,
  concave: string,
  mantle: string,
  useBinFeeder: boolean | undefined,
) => {
  const factors = {
    factorF100: 0,
    factorF90: 0,
    factorF85: 0,
  };
  if (
    concave === 'EC' ||
    concave === 'CX' ||
    concave === 'C' ||
    (concave === 'MC' && model === CrusherTypes.CH870i) ||
    (concave === 'MC' && model === CrusherTypes.CH880) ||
    (concave === 'MC' && model === CrusherTypes.CH890i)
  ) {
    if (
      useBinFeeder &&
      mantle != 'FFA' &&
      mantle != 'FFB' &&
      mantle != 'OptiAggS' &&
      mantle != 'OptiAggL'
    ) {
      factors.factorF100 = 1.4;
      factors.factorF90 = 0.95;
      factors.factorF85 = 0.65;
    } else {
      factors.factorF100 = 1.15;
      factors.factorF90 = 0.79;
      factors.factorF85 = 0.65;
    }
  } else {
    if (mantle === 'OB') {
      factors.factorF100 = 1.25;
      factors.factorF90 = 0.88;
      factors.factorF85 = 0.56;
    } else {
      factors.factorF100 = 1.1;
      factors.factorF90 = 0.88;
      factors.factorF85 = 0.56;
    }
  }
  return factors;
};

/**
 *
 * @param model
 * @param concave
 */
export const isCoarseCrushingChamber = (model: string, concave: string) => {
  if (
    concave === 'EC' ||
    concave === 'CX' ||
    concave === 'C' ||
    (concave === 'MC' && model === CrusherTypes.CH870i) ||
    (concave === 'MC' && model === CrusherTypes.CH880) ||
    (concave === 'MC' && model === CrusherTypes.CH890i)
  ) {
    return true;
  } else {
    return false;
  }
};

/**
 *
 * @param model
 * @param mantle
 * @param concave
 */
export const getRFO = (
  model: string,
  mantle: string,
  concave: string,
  unitInfoData: ConeCrusherCHUnitInfoDataMapInterface,
): number => {
  return unitInfoData[model].concaves[concave].mantles[mantle]?.rfo;
};

export const maxFeedF100 = (
  model: string,
  mantle: string,
  concave: string,
  binFeederUsed: boolean | undefined,
  unitInfoData: ConeCrusherCHUnitInfoDataMapInterface,
) => {
  return Math.ceil(
    getRFO(model, mantle, concave, unitInfoData) *
      GetMaxFeedSize(model, concave, mantle, binFeederUsed).factorF100,
  );
};

export const maxFeedF90 = (
  model: string,
  mantle: string,
  concave: string,
  binFeederUsed: boolean | undefined,
  unitInfoData: ConeCrusherCHUnitInfoDataMapInterface,
) => {
  return Math.ceil(
    getRFO(model, mantle, concave, unitInfoData) *
      GetMaxFeedSize(model, concave, mantle, binFeederUsed).factorF90,
  );
};

export const maxFeedF85 = (
  model: string,
  mantle: string,
  concave: string,
  binFeederUsed: boolean | undefined,
  unitInfoData: ConeCrusherCHUnitInfoDataMapInterface,
) => {
  return Math.ceil(
    getRFO(model, mantle, concave, unitInfoData) *
      GetMaxFeedSize(model, concave, mantle, binFeederUsed).factorF85,
  );
};

/**
 *
 * @param model
 */
export const GetD = (
  model: string,
  unitInfoData: ConeCrusherCHUnitInfoDataMapInterface,
): number => {
  return unitInfoData[model].d;
};

/**
 *
 *
 * @param model
 * @param mantle
 * @param concave
 * @param css
 * @param throw
 */
export const CapaConstCalc_P80method = (
  modelName: string,
  mantle: string,
  concave: string,
  selectedCSS: number,
  selectedThrow: number,
  unitInfoData: ConeCrusherCHUnitInfoDataMapInterface,
): number => {
  // Calculation values used to calculate nominal capacity
  const cssCalc: number = 0.04 * selectedCSS;

  const throwCalc: number =
    (0.7 * selectedThrow) / (0.8 * GetD(modelName, unitInfoData));
  let nominalCapacity = 0;

  const model = unitInfoData[modelName];
  const basicCapacity = model.concaves[concave].mantles[mantle]?.basicCapacity;
  nominalCapacity =
    basicCapacity *
    (cssCalc + 1) *
    0.8 *
    GetD(modelName, unitInfoData) *
    (0.3 + throwCalc);
  return Math.round(nominalCapacity);
};

/**
 *
 * @param crusher
 * @param concave
 * @param selectedThrow
 */
export const GetMotorPowerReq = (
  model: string,
  concave: string,
  selectedThrow: number,
  unitInfoData: ConeCrusherCHUnitInfoDataMapInterface,
): number => {
  const basicPower = unitInfoData[model].concaves[concave].basicPower;
  const calcThrow = 0.8 * selectedThrow;
  const calcMantleDiameter = 0.16 * GetD(model, unitInfoData);
  const firstCalc = basicPower * (calcThrow + calcMantleDiameter);
  const idlePower = unitInfoData[model].idlePower;
  const powerReq = (firstCalc + idlePower) / 1.1;
  return Math.round(powerReq);
};

/**
 *
 * @param model
 * @param concave
 */
export const GetFillerRing = (model: string, concave: string): string => {
  if (model === CrusherTypes.CH420) {
    if (
      concave === 'C' ||
      concave === 'M' ||
      concave === 'MF' ||
      concave === 'F' ||
      concave === 'EF'
    ) {
      return 'C, M, MF, F, EF';
    }
  } else if (model === CrusherTypes.CH430) {
    if (concave === 'C' || concave === 'MC' || concave === 'EF') {
      return 'C, MC, EF';
    }
  } else if (model === CrusherTypes.CH440 || model === CrusherTypes.CH442) {
    if (concave === 'MC' || concave === 'M' || concave === 'EF') {
      return 'MC, M, EF';
    }
  } else if (model === CrusherTypes.CH830i) {
    if (concave === 'C') {
      return 'C';
    } else if (concave === 'M' || concave === 'F' || concave === 'EF') {
      return 'M, F, EF';
    }
  } else if (model === CrusherTypes.CH840i) {
    if (concave === 'C' || concave === 'MC') {
      return 'C, MC';
    } else if (concave === 'M' || concave === 'MF' || concave === 'F') {
      return 'M, F';
    }
  } else if (model === CrusherTypes.CH660 || model === CrusherTypes.CH662) {
    if (concave === 'MC' || concave === 'M') {
      return 'MC, M';
    } else if (concave === 'MF' || concave === 'F') {
      return 'MF, F';
    }
  } else if (model === CrusherTypes.CH860i) {
    if (concave === 'MC') {
      return 'MC';
    } else if (concave === 'M') {
      return 'M';
    }
  } else if (model === CrusherTypes.CH870i) {
    if (concave === 'EC') {
      return 'EC';
    } else if (concave === 'C' || concave === 'MC') {
      return 'C, MC';
    } else if (
      concave === 'M' ||
      concave === 'MF' ||
      concave === 'F' ||
      concave === 'EF'
    ) {
      return 'M, MF, F, EF, HR';
    }
  } else if (model === CrusherTypes.CH880) {
    if (
      concave === 'MC' ||
      concave === 'F' ||
      concave === 'EFX' ||
      concave === 'EF' ||
      concave === 'EEF'
    ) {
      return 'MC, F, EFX, EF, EEF';
    } else if (concave === 'M') {
      return 'M';
    } else if (concave === 'MF') {
      return 'MF';
    }
  } else if (model === CrusherTypes.CH890i) {
    if (concave === 'MC' || concave === 'F') {
      return 'MC, F';
    } else if (concave === 'M') {
      return 'M';
    } else if (concave === 'MF') {
      return 'MF';
    }
  }
  return 'N/A';
};

/**
 *
 * @param model
 * @param concave
 * @param selThrow
 * @param availableBushing
 * @param availableBushing2
 */
export const getBushings = (
  model: string,
  concave: string,
  selectedThrow: number,
) => {
  const concaves = AvailableBushings[model].concaves;
  if (concaves[concave]?.[selectedThrow]) {
    return concaves[concave][selectedThrow];
  } else if (concaves.Other?.[selectedThrow]) {
    return concaves.Other[selectedThrow];
  } else {
    return [''];
  }
};
